import { useEffect, useState } from "react";
import {
  TranscodeStatus,
  getTranscodeStatus,
  isTranscodeActive,
} from "../api/upload.api";

export const TRANSCODE_POLL_INTERVAL_MS = 5000;
const MAX_CONSECUTIVE_ERRORS = 5;

/**
 * Polls GET /api/Asset/upload/transcode-status/{videoId} every 5 seconds while
 * the video is Queued / Processing / Encoding. Stops on a terminal status, on
 * repeated failures, when `videoId` is cleared, or on unmount.
 */
export function useTranscodeStatus(videoId: string | number | undefined | null) {
  const [status, setStatus] = useState<TranscodeStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    setStatus(null);
    setUnavailable(false);
    if (videoId === undefined || videoId === null || videoId === "") return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let errors = 0;

    const tick = async () => {
      try {
        const next = await getTranscodeStatus(videoId);
        if (cancelled) return;
        errors = 0;
        setStatus(next);
        if (isTranscodeActive(next.state)) {
          timer = setTimeout(tick, TRANSCODE_POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        errors += 1;
        if (errors >= MAX_CONSECUTIVE_ERRORS) {
          setUnavailable(true);
          return;
        }
        timer = setTimeout(tick, TRANSCODE_POLL_INTERVAL_MS);
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [videoId]);

  return { status, unavailable };
}
